'use client';
import React, { useState } from 'react';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}

export default function ContactSection() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" style={{ backgroundColor: '#0f2240' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Contact Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-0.5" style={{ backgroundColor: '#c9a84c' }} />
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#c9a84c', letterSpacing: '0.2em' }}>
                Contact Us
              </span>
            </div>
            <h2
              className="font-black mb-4 uppercase"
              style={{ color: '#ffffff', fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: '1.2', fontFamily: 'Lato, sans-serif' }}
            >
              Timber Point Capital Management
            </h2>
            <p className="text-base mb-8" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Identifying where institutional alternative investment resources can complement your{' '}
              <span style={{ color: '#c9a84c', fontStyle: 'italic' }}>existing capabilities.</span>
            </p>

            {/* Contact Details */}
            <div className="space-y-5">
              {/* Office */}
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}
                >
                  <svg width="18" height="18" fill="none" stroke="#c9a84c" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#c9a84c' }}>Office Location</p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>4 Westchester Park Drive</p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>Suite 100</p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>White Plains, NY 10604-3431</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(64,96,161,0.2)', border: '1px solid rgba(64,96,161,0.4)' }}
                >
                  <svg width="18" height="18" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6 6l.94-.94a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#c9a84c' }}>Phone</p>
                  <a href="tel:9148983038" className="text-sm hover:text-[#c9a84c] transition-colors" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    (914) 898-3038
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}
                >
                  <svg width="18" height="18" fill="none" stroke="#c9a84c" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#c9a84c' }}>Email</p>
                  <a href="mailto:info@timberpointcapital.com" className="text-sm hover:text-[#c9a84c] transition-colors" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    info@timberpointcapital.com
                  </a>
                </div>
              </div>

              {/* Website */}
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(64,96,161,0.2)', border: '1px solid rgba(64,96,161,0.4)' }}
                >
                  <svg width="18" height="18" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#c9a84c' }}>Website</p>
                  <a href="https://www.timberpointcapital.com" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-[#c9a84c] transition-colors" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    www.timberpointcapital.com
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-8">
              <a
                href="https://twitter.com/TPCM17"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border transition-colors hover:border-[#c9a84c] hover:text-[#c9a84c]"
                style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)' }}
                aria-label="Twitter"
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/timber-point-capital-management/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border transition-colors hover:border-[#c9a84c] hover:text-[#c9a84c]"
                style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)' }}
                aria-label="LinkedIn"
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
              <a
                href="https://www.youtube.com/channel/UCf7sy1gzszeEW3ANmTOiLMw/featured"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border transition-colors hover:border-[#c9a84c] hover:text-[#c9a84c]"
                style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)' }}
                aria-label="YouTube"
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
                  <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white p-8">
            <h3 className="font-bold mb-2" style={{ color: '#1a2744', fontSize: '1.25rem' }}>Request More Information</h3>
            <p className="text-sm mb-6" style={{ color: '#666' }}>
              Schedule a 30-minute working session to explore how TPCM Alternatives can complement your practice.
            </p>

            {submitted ? (
              <div className="py-12 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#c9a84c' }}>
                  <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h4 className="font-bold mb-2" style={{ color: '#1a2744' }}>Thank You!</h4>
                <p className="text-sm" style={{ color: '#666' }}>We&apos;ll be in touch within one business day.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#1a2744' }}>
                      First Name <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full border border-[#e0e4ec] px-3 py-2.5 text-sm focus:outline-none focus:border-[#4060a1] transition-colors"
                      style={{ fontFamily: 'Lato, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#1a2744' }}>
                      Last Name <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full border border-[#e0e4ec] px-3 py-2.5 text-sm focus:outline-none focus:border-[#4060a1] transition-colors"
                      style={{ fontFamily: 'Lato, sans-serif' }}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#1a2744' }}>
                    Email <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#e0e4ec] px-3 py-2.5 text-sm focus:outline-none focus:border-[#4060a1] transition-colors"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#1a2744' }}>
                    Company Name <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#e0e4ec] px-3 py-2.5 text-sm focus:outline-none focus:border-[#4060a1] transition-colors"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#1a2744' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border border-[#e0e4ec] px-3 py-2.5 text-sm focus:outline-none focus:border-[#4060a1] transition-colors"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#1a2744' }}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full border border-[#e0e4ec] px-3 py-2.5 text-sm focus:outline-none focus:border-[#4060a1] transition-colors resize-none"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                </div>
                {error && (
                  <div className="mb-4 px-4 py-3 text-sm" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 text-sm font-semibold text-white uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#4060a1', letterSpacing: '0.07em', fontFamily: 'Lato, sans-serif' }}
                >
                  {loading ? 'Sending...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
